/**
 * Collection Search & Filter Controller
 */

import { debounce } from './CollectionUI.js';

const SORT_MAP = {
    created_desc: { orderby: 'created_at', order: 'desc' },
    created_asc: { orderby: 'created_at', order: 'asc' },
    updated_desc: { orderby: 'updated_at', order: 'desc' },
    updated_asc: { orderby: 'updated_at', order: 'asc' },
    name_asc: { orderby: 'name', order: 'asc' },
    name_desc: { orderby: 'name', order: 'desc' },
    view_desc: { orderby: 'view_count', order: 'desc' },
};

export class CollectionSearch {
    constructor(rootElement, manager, api) {
        this.root = rootElement || document;
        this.manager = manager;
        this.api = api;
        this.debounceDelay = 1000; // Increased to 1000ms for fallback UX

        this.elements = {
            searchInput: this.root.querySelector('[data-collection-search]'),
            submitSearch: this.root.querySelector('[data-submit-search]'),
            clearSearch: this.root.querySelector('[data-clear-search]'),
            tagFilter: this.root.querySelector('[data-tag-filter]'),
            visibilityFilter: this.root.querySelector('[data-visibility-filter]'),
            sortFilter: this.root.querySelector('[data-sort-filter]'),
            activeFilters: this.root.querySelector('[data-active-filters]'),
        };

        this.filters = {
            search: '',
            tag: '',
            visibility: 'all',
            orderby: 'created_at',
            order: 'desc',
            sort: 'created_desc',
        };

        this.applyFiltersDebounced = debounce(() => this.applyFilters(), this.debounceDelay);

        this.init();
    }

    init() {
        this.attachEventListeners();
        this.loadAvailableTags();
    }

    attachEventListeners() {
        if (this.elements.searchInput) {
            // Remove automatic search on input, only update UI state
            this.elements.searchInput.addEventListener('input', (event) => {
                this.handleSearchInput(event.target.value);
            });

            // Handle Enter key press
            this.elements.searchInput.addEventListener('keydown', (event) => {
                if (event.key === 'Enter') {
                    event.preventDefault();
                    this.handleSearchSubmit();
                }
            });

            this.elements.searchInput.addEventListener('search', () => {
                if (this.elements.searchInput.value === '') {
                    this.handleClearSearch();
                }
            });
        }

        // Handle submit button click
        if (this.elements.submitSearch) {
            this.elements.submitSearch.addEventListener('click', () => {
                this.handleSearchSubmit();
            });
        }

        if (this.elements.clearSearch) {
            this.elements.clearSearch.addEventListener('click', () => {
                this.handleClearSearch();
            });
        }

        if (this.elements.tagFilter) {
            this.elements.tagFilter.addEventListener('change', (event) => {
                this.handleTagFilter(event.target.value);
            });
        }

        if (this.elements.visibilityFilter) {
            this.elements.visibilityFilter.addEventListener('change', (event) => {
                this.handleVisibilityFilter(event.target.value);
            });
        }

        if (this.elements.sortFilter) {
            this.elements.sortFilter.addEventListener('change', (event) => {
                this.handleSortFilter(event.target.value);
            });
        }

        this.manager.on('filters:change', ({ filters }) => {
            this.syncFromManager(filters);
        });
    }

    handleSearchInput(query = '') {
        const value = query.trim();
        this.filters.search = value;

        if (this.elements.clearSearch) {
            if (value.length > 0) {
                this.elements.clearSearch.classList.remove('opacity-0', 'pointer-events-none');
            } else {
                this.elements.clearSearch.classList.add('opacity-0', 'pointer-events-none');
            }
        }

        // Use debounced search as fallback for better UX
        this.applyFiltersDebounced();
    }

    handleSearchSubmit() {
        const value = this.elements.searchInput.value.trim();
        this.filters.search = value;
        
        // Cancel any pending debounced search
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
        }
        
        // Apply filters immediately
        this.applyFilters();
    }

    handleClearSearch() {
        this.filters.search = '';
        if (this.elements.searchInput) {
            this.elements.searchInput.value = '';
        }
        if (this.elements.clearSearch) {
            this.elements.clearSearch.classList.add('opacity-0', 'pointer-events-none');
        }
        this.applyFilters();
    }

    handleTagFilter(tag) {
        this.filters.tag = tag || '';
        this.applyFilters();
        this.updateActiveFilters();
    }

    handleVisibilityFilter(visibility) {
        this.filters.visibility = visibility || 'all';
        this.applyFilters();
        this.updateActiveFilters();
    }

    handleSortFilter(sortKey) {
        const mapping = SORT_MAP[sortKey] || SORT_MAP.created_desc;
        this.filters.sort = sortKey;
        this.filters.orderby = mapping.orderby;
        this.filters.order = mapping.order;
        this.applyFilters();
    }

    async applyFilters() {
        await this.manager.setFilters({
            search: this.filters.search,
            tag: this.filters.tag,
            visibility: this.filters.visibility,
            orderby: this.filters.orderby,
            order: this.filters.order,
        });
        this.updateActiveFilters();
    }

    async loadAvailableTags() {
        if (!this.api || !this.elements.tagFilter) {
            return;
        }

        try {
            const { tags } = await this.api.getTags();
            
            /* Clear existing options except the first one (default option) */
            const existingOptions = this.elements.tagFilter.querySelectorAll('option');
            for (let i = existingOptions.length - 1; i > 0; i--) {
                existingOptions[i].remove();
            }
            
            const fragment = document.createDocumentFragment();
            if (Array.isArray(tags)) {
                const sortedTags = [...tags].sort((a, b) => {
                    return a.name.localeCompare(b.name, 'vi', { sensitivity: 'base' });
                });

                sortedTags.forEach((tagItem) => {
                    const option = document.createElement('option');
                    option.value = tagItem.name;
                    option.textContent = `${tagItem.name} (${tagItem.count ?? 0})`;
                    option.dataset.usageCount = String(tagItem.count ?? 0);
                    fragment.appendChild(option);
                });
            }
            this.elements.tagFilter.appendChild(fragment);
        } catch (error) {
            // Fail silently – tags are optional.
            console.warn('Failed to load collection tags', error);
        }
    }

    updateActiveFilters() {
        if (!this.elements.activeFilters) {
            return;
        }

        const chips = [];
        if (this.filters.tag) {
            chips.push(
                `<span class="px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded-full">#${this.filters.tag}</span>`,
            );
        }
        if (this.filters.visibility && this.filters.visibility !== 'all') {
            const label = this.filters.visibility === 'public' ? 'Công khai' : 'Riêng tư';
            chips.push(
                `<span class="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full">${label}</span>`,
            );
        }

        this.elements.activeFilters.innerHTML = chips.join('');
    }

    syncFromManager(filters = {}) {
        if (typeof filters.search === 'string' && this.elements.searchInput) {
            this.elements.searchInput.value = filters.search;
            this.filters.search = filters.search;
        }
        if (typeof filters.tag === 'string' && this.elements.tagFilter) {
            this.elements.tagFilter.value = filters.tag;
            this.filters.tag = filters.tag;
        }
        if (typeof filters.visibility === 'string' && this.elements.visibilityFilter) {
            this.elements.visibilityFilter.value = filters.visibility === 'all' ? '' : filters.visibility;
            this.filters.visibility = filters.visibility;
        }
        if (typeof filters.orderby === 'string' && typeof filters.order === 'string') {
            const sortKey = Object.entries(SORT_MAP).find(([, value]) => {
                return value.orderby === filters.orderby && value.order === filters.order;
            });
            this.filters.orderby = filters.orderby;
            this.filters.order = filters.order;
            if (sortKey && this.elements.sortFilter) {
                this.filters.sort = sortKey[0];
                this.elements.sortFilter.value = sortKey[0];
            }
        }
        this.updateActiveFilters();
    }
}

export default CollectionSearch;
